import { isFileUIPart, isToolUIPart } from "ai";

import { isCheckpointTool } from "./checkpoint-part";
import { hasToolOutputImage } from "./tool-output";
import type {
  AnyToolPart,
  AnyUIMessage,
  AnyUIPart,
  StandsAlone,
} from "./types";
import { isContextTextPart } from "./workspace-context";

/**
 * A message body rendered as a flat list of items: either a fold of
 * consecutive tool calls (`tools`) or a single non-tool part (`part`).
 */
export type BodyItem =
  | { kind: "tools"; parts: AnyToolPart[]; key: string }
  | { kind: "part"; part: AnyUIPart; key: string };

/**
 * A tool call that must never fold into a collapsed run — because the member
 * has to act on it, or has to see it:
 *
 * - an open approval needs a human decision, and a group renders as a single
 *   spinner line; and
 * - a result carrying an image is what the agent is looking at, so hiding it
 *   inside a "7 tool calls" fold hides the very thing that shows progress; and
 * - a checkpoint is a marker across the transcript rather than a line in a run,
 *   so folding it away hides the event itself.
 *
 * Hosts extend this with their own parked shapes (a payment awaiting the
 * member's phone, an App tool parked on consent) via the `standsAlone` argument.
 */
function defaultStandsAlone(part: AnyToolPart): boolean {
  return (
    part.state === "approval-requested" ||
    hasToolOutputImage(part) ||
    isCheckpointTool(part)
  );
}

/**
 * Walk a message's parts and fold each maximal run of consecutive tool calls
 * into a single `tools` item, so the body renders one collapsible group per run
 * instead of a stack of pills. A part that renders something visible — text,
 * an attachment, a *streaming* reasoning block — ends the current run; invisible
 * parts (`step-start`, hidden context text, settled reasoning, and both source
 * kinds — those render once per message, see {@link collectSources}) are
 * transparent and neither render nor break a run, so tool calls separated only
 * by the SDK's per-step `step-start` markers — or by reasoning that has since
 * collapsed to nothing — still group together.
 *
 * A call that {@link StandsAlone} claims is the exception: it breaks the current
 * run and stands alone as its own single-part group, which renders the prominent
 * card at the top level. Once resolved it's an ordinary tool part and folds back
 * into a run like any other.
 */
export function groupBodyItems(
  message: AnyUIMessage,
  standsAlone: StandsAlone = defaultStandsAlone,
): BodyItem[] {
  const items: BodyItem[] = [];
  let run: AnyToolPart[] = [];
  let runKey = "";
  const flush = () => {
    if (run.length === 0) return;
    items.push({ kind: "tools", parts: run, key: runKey });
    run = [];
    runKey = "";
  };
  message.parts.forEach((part, i) => {
    if (isToolUIPart(part)) {
      if (defaultStandsAlone(part) || standsAlone(part)) {
        flush();
        items.push({
          kind: "tools",
          parts: [part],
          key: `${message.id}-alone-${i}`,
        });
        return;
      }
      if (run.length === 0) runKey = `${message.id}-tools-${i}`;
      run.push(part);
      return;
    }
    if (!isVisibleBreak(part)) return;
    flush();
    items.push({ kind: "part", part, key: `${message.id}-${i}` });
  });
  flush();
  return items;
}

/** One web page the assistant cited, as rendered in the message's source list. */
export interface MessageSource {
  url: string;
  /** The provider's page title, or the URL itself when it reports none. */
  label: string;
}

/**
 * The web sources cited anywhere in a message, in first-citation order.
 *
 * A provider emits one `source-url` part per citation — OpenAI's `web_search`
 * annotates every sentence it grounded — so the same page arrives many times
 * over a turn, and several searches across steps interleave their sources with
 * the text. Both collapse here: one entry per URL for the whole message, which
 * is the granularity the reader wants ("what did this answer stand on").
 */
export function collectSources(message: AnyUIMessage): MessageSource[] {
  const byUrl = new Map<string, MessageSource>();
  for (const part of message.parts) {
    if (part.type !== "source-url") continue;
    // A later citation of the same page may be the one that carries a title.
    if (byUrl.has(part.url) && !part.title) continue;
    byUrl.set(part.url, { url: part.url, label: part.title || part.url });
  }
  return [...byUrl.values()];
}

/**
 * Whether a non-tool part renders visible content, and so should end an
 * in-progress tool run. Mirrors what actually paints: visible text and
 * attachments always break; a reasoning part breaks only while `streaming`
 * (that's the only state `ReasoningView` renders — a settled one collapses to
 * nothing, so it must stay transparent or it would strand the tool groups it can
 * no longer be seen between). Everything else renders nothing and is treated as
 * transparent.
 *
 * This must agree with `renderPart` in `ChatPane`, because a part this call
 * declares invisible is dropped by {@link groupBodyItems} and never reaches the
 * renderer at all. When attachments were images-only the two happened to match;
 * once a PDF could be attached, disagreeing here silently swallowed it — the
 * member saw their own message with no sign of the file on it.
 */
export function isVisibleBreak(part: AnyUIPart): boolean {
  if (part.type === "text") return !isContextTextPart(part.text);
  // Every attachment paints: an image as a thumbnail, anything else as a chip.
  if (isFileUIPart(part)) return true;
  if (part.type === "reasoning") return part.state === "streaming";
  // Both structured data parts the shell renders paint their own card.
  if (part.type === "data-error" || part.type === "data-compaction")
    return true;
  return false;
}
